'use client';

import Link from 'next/link';
import { footerData } from '@/constants/footerData';

const ICON_STYLES =
  'w-10 h-10 rounded-lg bg-background flex items-center justify-center text-foreground/70 hover:text-primary hover:bg-primary/10 transition-colors';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { brand, quickLinks, legalLinks, socialLinks, bottomLinks } = footerData;
  const BrandIcon = brand.icon;

  const youtubeChannels = socialLinks.filter((link) => link.group === 'youtube');
  const otherSocial = socialLinks.filter((link) => link.group === 'other');

  return (
    <footer className="bg-card-bg border-t border-border mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BrandIcon className="text-primary" size={24} />
              <span className="text-xl font-bold text-foreground">
                {brand.name}
              </span>
            </div>
            <p className="text-sm text-foreground/70">{brand.description}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-foreground mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-foreground/70 hover:text-primary
                      transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-bold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-foreground/70 hover:text-primary
                      transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h3 className="font-bold text-foreground mb-4">Follow Us</h3>
            <div className="space-y-4">
              {/* YouTube Channels Grid */}
              <div>
                <p className="text-xs font-medium text-foreground/60 mb-3">
                  YouTube
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {youtubeChannels.map((social, idx) => {
                    const SocialIcon = social.icon;
                    return (
                      <a
                        key={idx}
                        href={social.href}
                        className={ICON_STYLES}
                        title={social.title}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <SocialIcon size={social.size} />
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Other Social Grid */}
              <div>
                <p className="text-xs font-medium text-foreground/60 mb-3">
                  Connect
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {otherSocial.map((social, idx) => {
                    const SocialIcon = social.icon;
                    return (
                      <a
                        key={idx}
                        href={social.href}
                        className={ICON_STYLES}
                        title={social.title}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <SocialIcon size={social.size} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border my-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-foreground/70">
            © {currentYear} {brand.name}. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            {bottomLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-foreground/70 hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
